import React, { useState, useRef, useEffect } from 'react';
import { Button, Typography } from '@primitives';
import styles from './styles.module.scss';
import {
  AlertIcon,
  DeleteIconOutlined,
  DoneIcon,
  LoadingOverlayTemplateIcon,
  CancelIcon,
  DownloadIcon,
  UploadIcon
} from '@icons';
import axiosInstance, { ApiKeys } from '@cp/utils/ApiHelper';
import { getApiUrl } from '@cp/utils/ApiHelper';
import LoadingIcon from '@icons/LoadingIcon';
import { BackendServiceabilityStatus, Downloadtype } from '../../types';
import SampleCsv, {
  onSampleCsvDownload
} from '@cp/pages/AgencyPincodeMapping/components/SampleCsv';
import cx from 'classnames';
import { toast } from '@primitives/Toast';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import SingleAutocompleteDropdown from '@cp/src/components/AutocompleteDropdown/SingleAutocompleteDropdown';
import { transformAgencies } from '../DataTableHeader';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/components/TooltipV2/TooltipV2';

enum UPLOAD_STATES {
  INITIAL = 'initial',
  UPLOADING = 'uploading',
  UPLOAD_SUCCESS = 'upload_success',
  UPLOAD_FAILED = 'upload_failed',
  ERROR = 'error'
}

type UploaderProps = {
  setSubmitButtonDisabled: (disabled: boolean) => void;
};

type InfoObject = {
  failedRecordsCount: number;
  totalRecordsCount: number;
  sourceFilePreSignedGetUrl: string;
  errorReportFilePreSignedGetUrl: string | null;
  malformedFile: boolean;
  limitBreached: boolean;
};

const initialState: InfoObject = {
  failedRecordsCount: 0,
  totalRecordsCount: 0,
  sourceFilePreSignedGetUrl: '',
  errorReportFilePreSignedGetUrl: null,
  malformedFile: false,
  limitBreached: false
};

const MAX_NUMBER_OF_RECORDS_ALLOWED = 3000;

const POLLING_FOR_STATUS_INTERVAL = 3 * 1000;

function Uploader({ setSubmitButtonDisabled }: UploaderProps) {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState(UPLOAD_STATES.INITIAL);
  const [file, setFile] = useState<File | null>(null);
  const [info, setInfo] = useState<InfoObject | null>(null);
  const [disabled, setDisabled] = useState(true);
  const [uploadReferenceId, setUploadReferenceId] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const agencies = useSelector((state: RootState) => state.agencyPincodeMapping.agencies);
  const pollingTimerIdRef = useRef<NodeJS.Timeout | null>(null);

  const selectedAgencyFromFilter = useSelector(
    (state: RootState) => state.agencyPincodeMapping.selectedAgency
  );
  const [selectedAgencyInUploader, setSelectedAgencyInUploader] = useState<string>('');
  const refFirstClicked = useRef(false);

  useEffect(() => {
    if (!refFirstClicked.current || !selectedAgencyInUploader) {
      setSelectedAgencyInUploader(selectedAgencyFromFilter);
    }
  }, [selectedAgencyFromFilter]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    if (!selectedAgencyInUploader) return toast('Please select an agency', { type: 'error' });

    if (event?.currentTarget?.files) {
      const fileUploaded = event?.currentTarget?.files[0];
      handleUpload(fileUploaded, selectedAgencyInUploader);
    }
  };

  const onMyServiceabilityDownload = (selectedAgency: string, downloadType: Downloadtype) => {
    if (!selectedAgency) {
      toast('Please select an agency first', { type: 'error' });
      return;
    }
    toast('Downloading file please wait', {
      type: 'info',
      icon: <DownloadIcon color={'var(--blue-base)'} />
    });
    const url = getApiUrl(ApiKeys.AGENCY_SERVICEABILITY_GET_STATIC_URLS);
    axiosInstance
      .get(url, { params: { downloadType, agencyCode: selectedAgency } })
      .then(res => {
        window.open(res?.data?.preSignedGetUrl, '_blank');
      })
      .catch(err => {
        toast('Unable to download file please try again', { type: 'error' });
      });
  };

  const handleReportDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const MAX_FILE_SIZE_IN_MB = 5;
  const MAX_FILE_SIZE = 1e6 * MAX_FILE_SIZE_IN_MB;
  const handleUpload = async (fileToUpload: File | null, agencyCode: string) => {
    if (!fileToUpload) {
      return null;
    }

    if (fileToUpload?.size > MAX_FILE_SIZE) {
      toast(`File size cannot exceed ${MAX_FILE_SIZE_IN_MB} MB`, { type: 'error' });
      if (hiddenFileInput?.current) {
        hiddenFileInput.current.value = '';
      }

      return null;
    }

    setUploadState(UPLOAD_STATES.UPLOADING);
    setFile(fileToUpload);
    const formData = new FormData();
    formData.append('file', fileToUpload as Blob);
    const url = getApiUrl(ApiKeys.GET_AGENCY_SERVICEABILITY_GET_PRESIGNED_URL);
    let referenceID = '';
    try {
      const presignedUploadUrl = await axiosInstance.get(url, { params: { agencyCode } });

      referenceID = presignedUploadUrl?.data?.referenceId;
      const uploadUrl = presignedUploadUrl?.data?.preSignedPutUrl;

      await axiosInstance.put(uploadUrl, fileToUpload);

      await axiosInstance.patch(
        getApiUrl(ApiKeys.AGENCY_SERVICEABILITY_UPLOAD_ACK),
        {},
        { params: { referenceId: referenceID } }
      );
    } catch (e) {
      setUploadState(UPLOAD_STATES.UPLOAD_FAILED);
      return;
    }

    const pollForStatusTimerId = setInterval(async () => {
      const statusUrl = getApiUrl(ApiKeys.AGENCY_SERVICEABILITY_UPLOAD_STATUS);
      try {
        setInfo({ ...initialState });
        const res = await axiosInstance.get(statusUrl, { params: { referenceId: referenceID } });
        setUploadReferenceId(referenceID);
        //setUploadReferenceId(referenceID)
        if (res.data?.status === BackendServiceabilityStatus.VALIDATION_FAILED) {
          setUploadState(UPLOAD_STATES.UPLOAD_FAILED);
          setInfo({
            failedRecordsCount: res?.data?.failedRecordsCount,
            totalRecordsCount: res?.data?.totalRecordsCount,
            sourceFilePreSignedGetUrl: res?.data?.sourceFilePreSignedGetUrl,
            errorReportFilePreSignedGetUrl: res?.data?.errorReportFilePreSignedGetUrl || null,
            malformedFile: false,
            limitBreached: false
          });
          clearInterval(pollForStatusTimerId);
        }

        if (res.data?.status === BackendServiceabilityStatus.VALIDATION_SUCCESS) {
          setUploadState(UPLOAD_STATES.UPLOAD_SUCCESS);
          setInfo({
            failedRecordsCount: res?.data?.failedRecordsCount || 0,
            totalRecordsCount: res?.data?.totalRecordsCount || 0,
            sourceFilePreSignedGetUrl: res?.data?.sourceFilePreSignedGetUrl,
            errorReportFilePreSignedGetUrl: res?.data?.errorReportFilePreSignedGetUrl || null,
            malformedFile: false,
            limitBreached: false
          });
          setDisabled(false);
          //setSubmitButtonDisabled(false);
          clearInterval(pollForStatusTimerId);
        }

        if (res.data?.status === BackendServiceabilityStatus.MALFORMED_FILE_UPLOADED) {
          setUploadState(UPLOAD_STATES.UPLOAD_FAILED);
          setInfo({
            failedRecordsCount: 0,
            totalRecordsCount: 0,
            sourceFilePreSignedGetUrl: res?.data?.sourceFilePreSignedGetUrl,
            errorReportFilePreSignedGetUrl: res?.data?.errorReportFilePreSignedGetUrl || null,
            malformedFile: true,
            limitBreached: false
          });
          setDisabled(true);
          //setSubmitButtonDisabled(false);
          clearInterval(pollForStatusTimerId);
        }
        if (res.data?.status === BackendServiceabilityStatus.MAX_ALLOWED_ROWS_LIMIT_BREACHED) {
          setUploadState(UPLOAD_STATES.UPLOAD_FAILED);
          setInfo({
            failedRecordsCount: 0,
            totalRecordsCount: 0,
            sourceFilePreSignedGetUrl: res?.data?.sourceFilePreSignedGetUrl,
            errorReportFilePreSignedGetUrl: res?.data?.errorReportFilePreSignedGetUrl || null,
            malformedFile: false,
            limitBreached: true
          });
          setDisabled(true);
          //setSubmitButtonDisabled(false);
          clearInterval(pollForStatusTimerId);
        }
      } catch (err) {
        setUploadState(UPLOAD_STATES.UPLOAD_FAILED);
        clearInterval(pollForStatusTimerId);
      }
    }, POLLING_FOR_STATUS_INTERVAL);

    pollingTimerIdRef.current = pollForStatusTimerId;
  };

  const onCancel = () => {
    setUploadState(UPLOAD_STATES.INITIAL);
    setFile(null);
    setInfo(null);
    if (hiddenFileInput && hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }
    clearInterval(pollingTimerIdRef.current as NodeJS.Timeout);
  };

  const handleOnSubmit = () => {
    setSubmitLoading(true);
    const url = getApiUrl(ApiKeys.AGENCY_SERVICABILTY_SUBMIT);
    axiosInstance.patch(url, {}, { params: { referenceId: uploadReferenceId } }).then(res => {
      setSubmitLoading(false);
      onCancel();
      setSubmitButtonDisabled(true);
      setInfo(null);
      if (hiddenFileInput && hiddenFileInput?.current) {
        hiddenFileInput.current.value = '';
      }
      toast('Serviceability successfully updated', { type: 'success' });
    });
  };

  const shouldAllowDelete = () => {
    if (submitLoading) return false;

    return (
      uploadState === UPLOAD_STATES.UPLOAD_SUCCESS || uploadState === UPLOAD_STATES.UPLOAD_FAILED
    );
  };

  const renderValidationSummary = (info: InfoObject) => {
    if (uploadState === UPLOAD_STATES.UPLOADING) return;

    const { failedRecordsCount, totalRecordsCount, malformedFile, limitBreached } = info;
    if (malformedFile) {
      return <div className="text-[color:var(--red-base)]">Invalid or corrupted file </div>;
    }

    if (limitBreached) {
      return (
        <div className="text-[color:var(--red-base)]">
          {' '}
          Max number of records allowed is {MAX_NUMBER_OF_RECORDS_ALLOWED} please try to split file.
        </div>
      );
    }

    if (!failedRecordsCount) {
      if (totalRecordsCount === 0) {
        return (
          <div className="text-[color:var(--red-base)]">
            You uploaded a blank file, all serviceability for selected agency will be removed.
          </div>
        );
      }
      return (
        <div className="text-[color:var(--green-base)]">
          All <strong>{totalRecordsCount}</strong> pincodes serviceability has been validated
        </div>
      );
    }

    return (
      <div className="text-[color:var(--red-base)]">
        <strong>{failedRecordsCount}</strong> invalid entries out of {totalRecordsCount} have been
        identified
      </div>
    );
  };

  const ProgressState = () => {
    return (
      <div>
        <div className={styles.mainContainer}>
          <div className={styles.infoSection}>
            <div className="icon-section">
              {uploadState === UPLOAD_STATES.UPLOADING ? <LoadingIcon /> : null}
              {uploadState === UPLOAD_STATES.UPLOAD_FAILED ? (
                <AlertIcon color={'var(--red-base)'} width={20} height={20} />
              ) : null}
              {uploadState === UPLOAD_STATES.UPLOAD_SUCCESS ? (
                <DoneIcon color={'var(--green-base)'} width={20} height={20} />
              ) : null}
            </div>
            <div>
              <Typography
                variant={'p3'}
                className={cx({
                  [styles.fileNameColorRed]: uploadState === UPLOAD_STATES.UPLOAD_FAILED
                })}
              >
                <Tooltip placement="top" hiddenPadding={0}>
                  <TooltipTrigger>
                    <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {file?.name}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className={styles.tooltip}>{file?.name}</TooltipContent>
                </Tooltip>
              </Typography>
              {info ? (
                <>
                  <Typography variant={'p4'} style={{ opacity: 0.7, paddingBottom: 8 }}>
                    {renderValidationSummary(info)}
                  </Typography>

                  {uploadState !== UPLOAD_STATES.UPLOADING && (
                    <Button
                      onClick={() => {
                        if (info?.errorReportFilePreSignedGetUrl) {
                          handleReportDownload(info?.errorReportFilePreSignedGetUrl);
                          return;
                        }
                        handleReportDownload(info?.sourceFilePreSignedGetUrl);
                      }}
                      variant="text"
                      startAdornment={<DownloadIcon color={'var(--blue-base)'} />}
                    >
                      {' '}
                      Download
                    </Button>
                  )}
                </>
              ) : null}
            </div>
          </div>
          <div className={styles.actionsSection}>
            {uploadState === UPLOAD_STATES.UPLOAD_FAILED ? (
              <LoadingOverlayTemplateIcon
                style={{ marginRight: '8px' }}
                width={20}
                height={20}
                onClick={() => handleUpload(file, selectedAgencyInUploader)}
                className="cursor-pointer"
              />
            ) : null}
            {shouldAllowDelete() ? (
              <DeleteIconOutlined
                style={{ marginRight: '8px' }}
                width={20}
                height={20}
                onClick={onCancel}
                className="cursor-pointer"
              />
            ) : null}
            {uploadState === UPLOAD_STATES.UPLOADING ? (
              <CancelIcon width={20} height={20} onClick={onCancel} className="cursor-pointer" />
            ) : null}
          </div>
        </div>
        {uploadState === UPLOAD_STATES.UPLOAD_FAILED ? (
          <div className="text-[color:var(--red-base)] mt-2"> Upload failed</div>
        ) : null}
      </div>
    );
  };

  const handleUploadClick = () => {
    hiddenFileInput?.current?.click();
  };

  const handleOnAgencyChange = (agency: string) => {
    refFirstClicked.current = true;
    setSelectedAgencyInUploader(agency);
  };

  const disableAgencySelection = () => {
    if (uploadState === UPLOAD_STATES.UPLOAD_SUCCESS) return false;
    return !!file;
  };

  return (
    <div className={styles.uploadWrapper}>
      <div>
        <div>
          <Typography variant="p3" style={{ marginTop: 8, marginBottom: 8 }}>
            Choose Agency
          </Typography>
          <SingleAutocompleteDropdown
            key={'upload_agencies'}
            placeholder={'Select Agency'}
            selectedOption={selectedAgencyInUploader || ''}
            options={transformAgencies(agencies)}
            onSelectionChange={option => handleOnAgencyChange(option?.value || '')}
            containerClasses={styles.dropdown}
            disabled={disableAgencySelection()}
          />

          {selectedAgencyInUploader && (
            <Button
              style={{ paddingTop: 8, textAlign: 'right' }}
              onClick={() =>
                onMyServiceabilityDownload(selectedAgencyInUploader, Downloadtype.MY_SERVICEBILITY)
              }
              variant="text"
              startAdornment={<DownloadIcon color={'var(--blue-base)'} />}
            >
              {' '}
              Current agency serviceability
            </Button>
          )}
        </div>

        {file ? (
          <ProgressState />
        ) : (
          <>
            <Typography variant="p3" style={{ marginTop: 32 }}>
              Upload updated file
            </Typography>
            <div className={cx(styles.mainContainer, styles.uploadContainer)}>
              <Button
                startAdornment={<UploadIcon color={'var(--blue-base)'} />}
                variant="text"
                disabled={!selectedAgencyInUploader}
                onClick={handleUploadClick}
              >
                Click to upload
              </Button>
              <input
                type="file"
                accept=".csv"
                onChange={handleChange}
                ref={hiddenFileInput}
                style={{ display: 'none' }} // Make the file input element invisible
              />
            </div>
          </>
        )}
        {uploadState !== UPLOAD_STATES.UPLOAD_SUCCESS && <SampleCsv />}
      </div>
      <div className={styles.actionButtonWrapper}>
        <Button
          variant="primary"
          disabled={
            disabled ||
            submitLoading ||
            uploadState !== UPLOAD_STATES.UPLOAD_SUCCESS ||
            !selectedAgencyInUploader
          }
          onClick={handleOnSubmit}
          fullWidth={true}
        >
          {submitLoading ? <LoadingIcon color={'var(--white-smoke)'} /> : <div> Submit </div>}
        </Button>
      </div>
    </div>
  );
}

export default Uploader;
